/**
 * MMOWreck - MMO mode wreck entity
 * Displays destroyed ship/station wrecks on the hex map with server-driven state.
 * Uses dedicated wreck sprites: `wreck-ship` for ships, `wreck-station` for stations.
 */

import Phaser from 'phaser';
import { axialToPixel, type HexCoord } from '../../shared/HexUtils';
import type { MMOWreckData } from '../../managers/MMOStateManager';

const WRECK_SIZE = 42;

const STATION_KEYWORDS = ['waypoint', 'depot', 'fortress', 'capital', 'station'];

const STATUS_TINTS: Record<string, number> = {
  towing: 0x4488ff,
  repairing: 0x44cc44,
};

export class MMOWreck extends Phaser.GameObjects.Container {
  public id: string;
  public ownerId: string;
  public unitType: string;
  public hexCoord: HexCoord;
  public towStatus: string;
  public repairStatus: string;
  public destroyedAt: string;
  public expiresAt: string;

  private wreckSprite: Phaser.GameObjects.Image | null = null;
  private wreckGraphics: Phaser.GameObjects.Graphics;
  private typeLabel: Phaser.GameObjects.Text;
  private selectionIndicator: Phaser.GameObjects.Graphics;
  private damageOverlay: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, data: MMOWreckData) {
    const pixel = axialToPixel(data.position.q, data.position.r);
    super(scene, pixel.x, pixel.y);

    this.id = data.id;
    this.ownerId = data.ownerId;
    this.unitType = data.unitType;
    this.hexCoord = data.position;
    this.towStatus = data.towStatus;
    this.repairStatus = data.repairStatus;
    this.destroyedAt = data.destroyedAt;
    this.expiresAt = data.expiresAt;

    this.selectionIndicator = this.scene.add.graphics();
    this.selectionIndicator.setVisible(false);
    this.add(this.selectionIndicator);

    this.wreckGraphics = this.scene.add.graphics();
    this.add(this.wreckGraphics);

    this.damageOverlay = this.scene.add.graphics();
    this.add(this.damageOverlay);

    this.typeLabel = this.scene.add.text(0, 26, '', {
      fontSize: '9px',
      color: '#999999',
    });
    this.typeLabel.setOrigin(0.5);
    this.typeLabel.setVisible(false);
    this.add(this.typeLabel);

    this.drawVisuals();
    this.setDepth(3);

    // Nudge to 48px minimum on touch devices (iOS/Android guidelines)
    const _isCoarseWreck = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(pointer: coarse)').matches;
    const _wreckHit = _isCoarseWreck ? 48 : 44;
    this.setSize(_wreckHit, _wreckHit);
    this.setInteractive(
      new Phaser.Geom.Rectangle(-_wreckHit / 2, -_wreckHit / 2, _wreckHit, _wreckHit),
      Phaser.Geom.Rectangle.Contains,
    );

    // NOTE: No sprite-level 'pointerdown' handler — see MMOUnit.ts for rationale.
    // Selection is handled in MMOGameScene.onPointerUp via getEntityAtWorldPoint().
  }

  public updateFromServer(data: MMOWreckData): void {
    if (!this.scene || !this.scene.sys) return;

    this.ownerId = data.ownerId;
    this.unitType = data.unitType;
    this.towStatus = data.towStatus;
    this.repairStatus = data.repairStatus;
    this.destroyedAt = data.destroyedAt;
    this.expiresAt = data.expiresAt;

    if (data.position.q !== this.hexCoord.q || data.position.r !== this.hexCoord.r) {
      this.hexCoord = data.position;
      const pixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);
      this.setPosition(pixel.x, pixel.y);
    }

    this.drawVisuals();
  }

  public setVisibility(fullyVisible: boolean, explored: boolean): void {
    if (!explored) {
      this.setAlpha(0);
    } else if (!fullyVisible) {
      this.setAlpha(0.3);
    } else {
      this.setAlpha(1);
    }
  }

  public setSelected(selected: boolean): void {
    this.selectionIndicator.setVisible(selected);
  }

  private isStationWreck(): boolean {
    const lower = this.unitType.toLowerCase();
    return STATION_KEYWORDS.some(k => lower.includes(k));
  }

  private resolveSpriteKey(): string {
    const isStation = this.isStationWreck();
    if (isStation && this.scene.textures.exists('wreck-station')) return 'wreck-station';
    if (!isStation && this.scene.textures.exists('wreck-ship')) return 'wreck-ship';
    // Fallback to derelict
    if (this.scene.textures.exists('derelict')) return 'derelict';
    return '';
  }

  private drawVisuals(): void {
    this.wreckGraphics.clear();
    this.damageOverlay.clear();

    const spriteKey = this.resolveSpriteKey();

    if (spriteKey) {
      if (this.wreckSprite) {
        if (this.wreckSprite.texture.key !== spriteKey) {
          this.wreckSprite.setTexture(spriteKey);
        }
      } else {
        this.wreckSprite = this.scene.add.image(0, 0, spriteKey);
        this.wreckSprite.setDisplaySize(WRECK_SIZE, WRECK_SIZE);
        this.add(this.wreckSprite);
        this.moveTo(this.wreckSprite, 1);
      }

      // Status tint: towing=blue, repairing=green, no tint otherwise (sprites already look wrecked)
      const statusTint = STATUS_TINTS[this.towStatus] || STATUS_TINTS[this.repairStatus];
      if (statusTint) {
        this.wreckSprite.setTint(statusTint);
        this.wreckSprite.setAlpha(0.85);
      } else {
        this.wreckSprite.clearTint();
        this.wreckSprite.setAlpha(0.9);
      }

    } else {
      // Programmatic fallback if no sprite available
      if (this.wreckSprite) {
        this.wreckSprite.destroy();
        this.wreckSprite = null;
      }
      this.drawFallback();
    }

    this.typeLabel.setText(this.unitType);

    // Selection ring
    this.selectionIndicator.clear();
    this.selectionIndicator.lineStyle(2, 0x44ff44, 1);
    this.selectionIndicator.strokeCircle(0, 0, 24);
  }

  private drawFallback(): void {
    const color = this.towStatus === 'towing' ? 0x4488ff :
                  this.repairStatus === 'repairing' ? 0x44cc44 : 0x664444;

    this.wreckGraphics.fillStyle(color, 0.8);
    this.wreckGraphics.beginPath();
    this.wreckGraphics.moveTo(-12, -8);
    this.wreckGraphics.lineTo(-4, -14);
    this.wreckGraphics.lineTo(2, -6);
    this.wreckGraphics.lineTo(-2, 2);
    this.wreckGraphics.lineTo(-10, 4);
    this.wreckGraphics.closePath();
    this.wreckGraphics.fillPath();

    this.wreckGraphics.beginPath();
    this.wreckGraphics.moveTo(4, -4);
    this.wreckGraphics.lineTo(14, -10);
    this.wreckGraphics.lineTo(12, 2);
    this.wreckGraphics.lineTo(6, 8);
    this.wreckGraphics.lineTo(2, 4);
    this.wreckGraphics.closePath();
    this.wreckGraphics.fillPath();

    this.wreckGraphics.beginPath();
    this.wreckGraphics.moveTo(-6, 6);
    this.wreckGraphics.lineTo(4, 6);
    this.wreckGraphics.lineTo(8, 14);
    this.wreckGraphics.lineTo(-4, 12);
    this.wreckGraphics.closePath();
    this.wreckGraphics.fillPath();

    this.wreckGraphics.lineStyle(1, 0x888888, 0.5);
    this.wreckGraphics.strokeCircle(0, 0, 16);

    this.wreckGraphics.lineStyle(1.5, 0xff4444, 0.6);
    this.wreckGraphics.beginPath();
    this.wreckGraphics.moveTo(-4, -4);
    this.wreckGraphics.lineTo(4, 4);
    this.wreckGraphics.moveTo(4, -4);
    this.wreckGraphics.lineTo(-4, 4);
    this.wreckGraphics.strokePath();
  }

  public destroy(fromScene?: boolean): void {
    super.destroy(fromScene);
  }
}
